import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    Container,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Paper,
    useTheme,
    useMediaQuery,
    Box,
    Alert,
    Stack,
    Chip,
    Divider,
    FormControlLabel,
    Switch,
    IconButton,
    MenuItem,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import SubtitlesRoundedIcon from "@mui/icons-material/SubtitlesRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import axios from "axios";
import { API_URL } from "../../config";
import { motion } from "framer-motion";

const RECORD_NAME_MAX_LENGTH = 25;
const SURVEY_QUESTION_TYPES = {
    TEXT: "text",
    STAR: "star",
    EMOJI: "emoji",
    THUMBS: "thumbs",
    SELECTION: "selection",
};

const parseQrSettings = (value) => {
    if (!value) {
        return {};
    }

    if (typeof value === "object") {
        return value;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        console.error("Unable to parse QR settings", error);
        return {};
    }
};

const normalizeSurveyQuestion = (question) => {
    if (typeof question === "string") {
        const label = question.trim();
        return label ? { type: SURVEY_QUESTION_TYPES.TEXT, label, options: [], optionsInput: "" } : null;
    }

    if (!question || typeof question !== "object") {
        return null;
    }

    const type = [
        SURVEY_QUESTION_TYPES.TEXT,
        SURVEY_QUESTION_TYPES.STAR,
        SURVEY_QUESTION_TYPES.EMOJI,
        SURVEY_QUESTION_TYPES.THUMBS,
        SURVEY_QUESTION_TYPES.SELECTION,
    ].includes(question.type)
        ? question.type
        : SURVEY_QUESTION_TYPES.TEXT;
    const label = String(question.label || "").trim();
    const options = Array.isArray(question.options)
        ? question.options.map((option) => String(option || "").trim()).filter(Boolean)
        : [];

    if (!label) {
        return null;
    }

    const normalizedOptions = type === SURVEY_QUESTION_TYPES.SELECTION ? options : [];

    return {
        type,
        label,
        options: normalizedOptions,
        optionsInput: normalizedOptions.join(", "),
    };
};

const defaultSurveyQuestion = () => ({
    type: SURVEY_QUESTION_TYPES.TEXT,
    label: "",
    options: [],
    optionsInput: "",
});

const defaultSurveyCompletionRewards = () => ({
    giftVoucher: "",
    discountCode: "",
    prizeDraw: "",
    giftLimit: "",
    distributionMode: "sequential",
});

const URL_TYPE_OPTIONS = [
    { value: "url", label: "Website URL" },
    { value: "service", label: "Social / Common Service" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "sms", label: "SMS" },
    { value: "phone", label: "Phone" },
    { value: "email", label: "Email" },
    { value: "geo", label: "Location (Geo)" },
    { value: "bitcoin", label: "Bitcoin" },
    { value: "facetime", label: "FaceTime" },
];
const SOCIAL_SERVICE_OPTIONS = [
    { label: "Airbnb", value: "airbnb", base: "https://www.airbnb.com/users/show/" },
    { label: "Discord", value: "discord", base: "https://discord.gg/" },
    { label: "DoorDash", value: "doordash", base: "https://www.doordash.com/store/" },
    { label: "Facebook", value: "facebook", base: "https://www.facebook.com/" },
    { label: "Instagram", value: "instagram", base: "https://www.instagram.com/" },
    { label: "LinkedIn", value: "linkedin", base: "https://www.linkedin.com/in/" },
    { label: "Pinterest", value: "pinterest", base: "https://www.pinterest.com/" },
    { label: "Snapchat", value: "snapchat", base: "https://www.snapchat.com/add/" },
    { label: "Telegram", value: "telegram", base: "https://t.me/" },
    { label: "TikTok", value: "tiktok", base: "https://www.tiktok.com/@" },
    { label: "X (Twitter)", value: "x", base: "https://x.com/" },
    { label: "Yelp", value: "yelp", base: "https://www.yelp.com/biz/" },
    { label: "YouTube", value: "youtube", base: "https://www.youtube.com/@" },
].sort((a, b) => a.label.localeCompare(b.label));

const SORTED_URL_TYPE_OPTIONS = [...URL_TYPE_OPTIONS].sort((a, b) => a.label.localeCompare(b.label));

const defaultUrlFields = () => ({
    url: "",
    waNumber: "",
    waMessage: "",
    smsNumber: "",
    smsMessage: "",
    phone: "",
    email: "",
    subject: "",
    body: "",
    latitude: "",
    longitude: "",
    wallet: "",
    amount: "",
    label: "",
    message: "",
    faceTimeHandle: "",
    service: "airbnb",
    serviceHandle: "",
    serviceCustomUrl: "",
});

const detectUrlTypeAndFields = (value) => {
    const fields = defaultUrlFields();
    const input = String(value || "").trim();
    fields.url = input;

    if (!input) {
        return { type: "url", fields };
    }

    if (input.startsWith("https://wa.me/")) {
        try {
            const parsed = new URL(input);
            fields.waNumber = parsed.pathname.replace(/\//g, "");
            fields.waMessage = parsed.searchParams.get("text") || "";
        } catch (error) {
            console.error("Failed to parse WhatsApp URL", error);
        }
        return { type: "whatsapp", fields };
    }

    if (input.startsWith("sms:")) {
        const [schemePart, queryString = ""] = input.replace(/^sms:/, "").split("?");
        fields.smsNumber = schemePart || "";
        const params = new URLSearchParams(queryString);
        fields.smsMessage = params.get("body") || "";
        return { type: "sms", fields };
    }

    if (input.startsWith("tel:")) {
        fields.phone = input.replace(/^tel:/, "");
        return { type: "phone", fields };
    }

    if (input.startsWith("mailto:")) {
        const afterPrefix = input.replace(/^mailto:/, "");
        const [address, queryString = ""] = afterPrefix.split("?");
        const params = new URLSearchParams(queryString);
        fields.email = address || "";
        fields.subject = params.get("subject") || "";
        fields.body = params.get("body") || "";
        return { type: "email", fields };
    }

    if (input.startsWith("geo:")) {
        const coords = input.replace(/^geo:/, "").split(",");
        fields.latitude = coords[0] || "";
        fields.longitude = coords[1] || "";
        return { type: "geo", fields };
    }

    if (input.startsWith("bitcoin:")) {
        const afterPrefix = input.replace(/^bitcoin:/, "");
        const [wallet, queryString = ""] = afterPrefix.split("?");
        const params = new URLSearchParams(queryString);
        fields.wallet = wallet || "";
        fields.amount = params.get("amount") || "";
        fields.label = params.get("label") || "";
        fields.message = params.get("message") || "";
        return { type: "bitcoin", fields };
    }

    if (input.startsWith("facetime:")) {
        fields.faceTimeHandle = input.replace(/^facetime:/, "");
        return { type: "facetime", fields };
    }

    const matchedService = SOCIAL_SERVICE_OPTIONS.find((serviceOption) => input.startsWith(serviceOption.base));
    if (matchedService) {
        fields.service = matchedService.value;
        fields.serviceHandle = input.replace(matchedService.base, "").replace(/^\/+/, "");
        fields.serviceCustomUrl = "";
        return { type: "service", fields };
    }

    return { type: "url", fields };
};

const buildUrlFromFields = (type, fields) => {
    if (type === "url") {
        return (fields.url || "").trim();
    }
    if (type === "whatsapp") {
        return `https://wa.me/${(fields.waNumber || "").replace(/\D/g, "")}?text=${encodeURIComponent(fields.waMessage || "")}`;
    }
    if (type === "sms") {
        const query = new URLSearchParams();
        if (fields.smsMessage) query.set("body", fields.smsMessage);
        return `sms:${fields.smsNumber || ""}${query.toString() ? `?${query.toString()}` : ""}`;
    }
    if (type === "phone") {
        return `tel:${fields.phone || ""}`;
    }
    if (type === "email") {
        const query = new URLSearchParams();
        if (fields.subject) query.set("subject", fields.subject);
        if (fields.body) query.set("body", fields.body);
        return `mailto:${fields.email || ""}${query.toString() ? `?${query.toString()}` : ""}`;
    }
    if (type === "geo") {
        return `geo:${fields.latitude || ""},${fields.longitude || ""}`;
    }
    if (type === "bitcoin") {
        const query = new URLSearchParams();
        if (fields.amount) query.set("amount", fields.amount);
        if (fields.label) query.set("label", fields.label);
        if (fields.message) query.set("message", fields.message);
        return `bitcoin:${fields.wallet || ""}${query.toString() ? `?${query.toString()}` : ""}`;
    }
    if (type === "facetime") {
        return `facetime:${fields.faceTimeHandle || ""}`;
    }
    if (type === "service") {
        if (fields.serviceCustomUrl) return (fields.serviceCustomUrl || "").trim();
        const selected = SOCIAL_SERVICE_OPTIONS.find((item) => item.value === fields.service);
        return `${selected?.base || "https://"}${(fields.serviceHandle || "").replace(/^\/+/, "")}`;
    }

    return (fields.url || "").trim();
};

const EditCatalogPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const { id, category } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const numericCategory = Number(category);
    const isUrlRecord = numericCategory === 2;
    const isWebsiteRecord = numericCategory === 3;
    const isSurveyRecord = numericCategory === 4;
    const isFileRecord = !isUrlRecord && !isWebsiteRecord && !isSurveyRecord;

    const [recordName, setRecordName] = useState("");
    const [websiteHeader, setWebsiteHeader] = useState("");
    const [websiteSubheader, setWebsiteSubheader] = useState("");
    const [menuFile, setMenuFile] = useState(null);
    const [urlType, setUrlType] = useState("url");
    const [urlFields, setUrlFields] = useState(defaultUrlFields());
    const [surveyHeader, setSurveyHeader] = useState("");
    const [surveySubheader, setSurveySubheader] = useState("");
    const [surveyRequireLogin, setSurveyRequireLogin] = useState(false);
    const [surveyAlertEmail, setSurveyAlertEmail] = useState("");
    const [surveyQuestions, setSurveyQuestions] = useState([defaultSurveyQuestion()]);
    const [surveyCompletionRewards, setSurveyCompletionRewards] = useState(defaultSurveyCompletionRewards());
    const [surveyResponses, setSurveyResponses] = useState([]);
    const [websiteVisibleRecordIds, setWebsiteVisibleRecordIds] = useState([]);
    const [websiteRecordOptions, setWebsiteRecordOptions] = useState([]);
    const [passwordProtected, setPasswordProtected] = useState(false);
    const [accessPassword, setAccessPassword] = useState("");
    const [hasExistingPassword, setHasExistingPassword] = useState(false);
    const [responsesLoading, setResponsesLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const fetchRecord = async () => {
            try {
                const response = await axios.get(`${API_URL}get_restaurant.php?id=${id}`, {
                    withCredentials: true,
                });
                const data = response.data || {};
                const qrSettings = parseQrSettings(data.qr_settings);
                const detectedUrl = detectUrlTypeAndFields(data.menu_link || "");

                setRecordName(data.name || "");
                setUrlType(isUrlRecord ? detectedUrl.type : "url");
                setUrlFields(isUrlRecord ? detectedUrl.fields : defaultUrlFields());
                setWebsiteHeader(qrSettings.websiteHeader || "");
                setWebsiteSubheader(qrSettings.websiteSubheader || "");
                setSurveyHeader(qrSettings.surveyHeader || "");
                setSurveySubheader(qrSettings.surveySubheader || "");
                setSurveyRequireLogin(Boolean(qrSettings.surveyRequireLogin));
                setSurveyAlertEmail(qrSettings.surveyAlertEmail || "");
                const configuredRewards = qrSettings.completionIncentives || qrSettings.surveyCompletionRewards || {};
                setSurveyCompletionRewards({
                    giftVoucher: String(configuredRewards.giftVoucher || ""),
                    discountCode: String(configuredRewards.discountCode || ""),
                    prizeDraw: String(configuredRewards.prizeDraw || ""),
                    giftLimit: configuredRewards.giftLimit > 0 ? String(configuredRewards.giftLimit) : "",
                    distributionMode: configuredRewards.distributionMode === "random" ? "random" : "sequential",
                });
                const normalizedQuestions = Array.isArray(qrSettings.surveyQuestions)
                    ? qrSettings.surveyQuestions.map(normalizeSurveyQuestion).filter(Boolean)
                    : [];
                setSurveyQuestions(normalizedQuestions.length > 0 ? normalizedQuestions : [defaultSurveyQuestion()]);
                const visibleIds = Array.isArray(qrSettings.websiteVisibleRecordIds)
                    ? qrSettings.websiteVisibleRecordIds.map((recordId) => Number(recordId)).filter((recordId) => Number.isInteger(recordId) && recordId > 0)
                    : [];
                setWebsiteVisibleRecordIds(visibleIds);
                const isPasswordEnabled = Boolean(qrSettings.passwordProtected);
                setPasswordProtected(isPasswordEnabled);
                setHasExistingPassword(isPasswordEnabled && Boolean(qrSettings.accessPasswordHash));
                setAccessPassword("");
            } catch (error) {
                console.error("Error loading record:", error);
                setErrorMessage(error.response?.data?.message || "Failed to load the record.");
            } finally {
                setInitialLoading(false);
            }
        };

        fetchRecord();
    }, [id, isUrlRecord]);

    useEffect(() => {
        const fetchResponses = async () => {
            if (!isSurveyRecord) {
                return;
            }

            setResponsesLoading(true);
            try {
                const response = await axios.get(`${API_URL}get_survey_responses.php?record_id=${id}`, {
                    withCredentials: true,
                });
                setSurveyResponses(Array.isArray(response.data?.responses) ? response.data.responses : []);
            } catch (error) {
                console.error("Error loading survey responses:", error);
            } finally {
                setResponsesLoading(false);
            }
        };

        fetchResponses();
    }, [id, isSurveyRecord]);


    useEffect(() => {
        const fetchWebsiteOptions = async () => {
            if (!isWebsiteRecord) {
                return;
            }

            try {
                const response = await axios.get(API_URL + "get_restaurants.php", {
                    withCredentials: true,
                });

                const records = Array.isArray(response.data) ? response.data : [];
                setWebsiteRecordOptions(records.filter((record) => Number(record.CategoryID) !== 3));
            } catch (error) {
                console.error("Error loading website record options:", error);
            }
        };

        fetchWebsiteOptions();
    }, [isWebsiteRecord]);

    const urlRecordTypeCopy = useMemo(() => {
        const typeCopyMap = {
            url: { label: "URL", eyebrow: "URL destination" },
            whatsapp: { label: "WhatsApp", eyebrow: "WhatsApp destination" },
            sms: { label: "SMS", eyebrow: "SMS destination" },
            phone: { label: "Phone", eyebrow: "Phone destination" },
            email: { label: "Email", eyebrow: "Email destination" },
            geo: { label: "Location", eyebrow: "Location destination" },
            bitcoin: { label: "Bitcoin", eyebrow: "Bitcoin destination" },
            facetime: { label: "FaceTime", eyebrow: "FaceTime destination" },
            service: { label: "Service", eyebrow: "Service destination" },
        };

        return typeCopyMap[urlType] || typeCopyMap.url;
    }, [urlType]);

    const pageTitle = useMemo(() => {
        if (isWebsiteRecord) return "Edit Website Record";
        if (isSurveyRecord) return "Edit Survey Record";
        if (isUrlRecord) return `Edit ${urlRecordTypeCopy.label} Record`;
        return "Edit File Record";
    }, [isUrlRecord, isWebsiteRecord, isSurveyRecord, urlRecordTypeCopy]);

    const submitLabel = useMemo(() => {
        if (loading) return "Saving...";
        if (isWebsiteRecord) return "Save Website Record";
        if (isSurveyRecord) return "Save Survey Record";
        if (isUrlRecord) return `Update ${urlRecordTypeCopy.label}`;
        return "Update File Record";
    }, [isUrlRecord, isWebsiteRecord, isSurveyRecord, loading, urlRecordTypeCopy]);

    const recordTypeIcon = useMemo(() => {
        if (isWebsiteRecord) return <LanguageRoundedIcon fontSize="small" />;
        if (isSurveyRecord) return <SubtitlesRoundedIcon fontSize="small" />;
        if (isUrlRecord) return <LinkRoundedIcon fontSize="small" />;
        return <DescriptionRoundedIcon fontSize="small" />;
    }, [isUrlRecord, isWebsiteRecord, isSurveyRecord]);

    const recordTypeSummary = useMemo(() => {
        if (isWebsiteRecord) {
            return {
                eyebrow: "Website landing page",
                description: "Refresh the public landing experience with a stronger headline, clearer supporting copy, and a more polished first impression.",
                tips: [
                    "Use a short page title that instantly tells visitors what they can access.",
                    "Write supporting text that explains what the linked records help people do.",
                ],
            };
        }

        if (isSurveyRecord) {
            return {
                eyebrow: "Survey experience",
                description: "Customize your survey questions, enforce login when needed, and review real responses from people who scan this QR.",
                tips: [
                    "Put one question per line for clear response collection.",
                    "Toggle login requirement on when responses must come from authenticated users.",
                ],
            };
        }

        if (isUrlRecord) {
            return {
                eyebrow: urlRecordTypeCopy.eyebrow,
                description: `Update the label and destination behind this ${urlRecordTypeCopy.label.toLowerCase()} QR so scans always open the right experience.`,
                tips: [
                    "Keep the record name easy to recognize in your dashboard.",
                    urlType === "url"
                        ? "Paste the full URL, including https://, for reliable redirects."
                        : `Double-check the ${urlRecordTypeCopy.label.toLowerCase()} details before saving so scanners land in the right place.`,
                ],
            };
        }

        return {
            eyebrow: "File delivery",
            description: "Replace the stored file or update its label so the record stays current for everyone who scans it.",
            tips: [
                "Use a descriptive file name so teammates can identify it quickly.",
                "Only upload a new file when you want to replace the current document.",
            ],
        };
    }, [isUrlRecord, isWebsiteRecord, isSurveyRecord, urlRecordTypeCopy, urlType]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");
        setErrorMessage("");

        const trimmedRecordName = recordName.trim();

        if (trimmedRecordName.length > RECORD_NAME_MAX_LENGTH) {
            setErrorMessage(`Record name must be ${RECORD_NAME_MAX_LENGTH} characters or fewer.`);
            setLoading(false);
            return;
        }

        if (isUrlRecord) {
            const builtUrl = buildUrlFromFields(urlType, urlFields).trim();
            if (!builtUrl) {
                setErrorMessage("Please fill in the required URL details.");
                setLoading(false);
                return;
            }
        }

        if (passwordProtected && !hasExistingPassword && !accessPassword.trim()) {
            setErrorMessage("Please set an access password before enabling password protection.");
            setLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append("restaurant_id", id);
        formData.append("name", trimmedRecordName);
        formData.append("website_header", websiteHeader);
        formData.append("website_subheader", websiteSubheader);
        formData.append("survey_header", surveyHeader);
        formData.append("survey_subheader", surveySubheader);
        formData.append("survey_requires_login", String(surveyRequireLogin));
        formData.append("survey_alert_email", surveyAlertEmail.trim());
        formData.append("survey_gift_voucher", (surveyCompletionRewards.giftVoucher || "").trim());
        formData.append("survey_discount_code", (surveyCompletionRewards.discountCode || "").trim());
        formData.append("survey_prize_draw", (surveyCompletionRewards.prizeDraw || "").trim());
        formData.append("survey_gift_limit", String(surveyCompletionRewards.giftLimit || "").trim());
        formData.append("survey_gift_distribution", surveyCompletionRewards.distributionMode || "sequential");
        const cleanedSurveyQuestions = surveyQuestions
            .map((question) => normalizeSurveyQuestion(question))
            .filter(Boolean);
        formData.append("survey_questions", JSON.stringify(cleanedSurveyQuestions));
        formData.append("website_visible_record_ids", JSON.stringify(websiteVisibleRecordIds));
        formData.append("qr_password_protected", String(passwordProtected));
        formData.append("qr_access_password", accessPassword);

        if (isUrlRecord) {
            formData.append("url", buildUrlFromFields(urlType, urlFields).trim());
        }

        if (isFileRecord && menuFile) {
            formData.append("menu_file", menuFile);
        }

        try {
            const response = await axios.post(API_URL + "update_menu.php", formData, {
                headers: { "Content-Type": "multipart/form-data" },
                withCredentials: true,
            });
            setMessage(response.data.message);
            if (passwordProtected) {
                setHasExistingPassword(true);
                setAccessPassword("");
            } else {
                setHasExistingPassword(false);
                setAccessPassword("");
            }

            setTimeout(() => navigate("/list"), 1500);
        } catch (error) {
            console.error("Error updating record:", error);
            setErrorMessage(error.response?.data?.message || "Failed to update the record.");
        } finally {
            setLoading(false);
        }
    };

    const handleSurveyQuestionChange = (index, field, value) => {
        setSurveyQuestions((prev) => prev.map((question, questionIndex) => {
            if (questionIndex !== index) {
                return question;
            }

            if (field === "type") {
                return {
                    ...question,
                    type: value,
                    options: value === SURVEY_QUESTION_TYPES.SELECTION ? (question.options || []) : [],
                    optionsInput: value === SURVEY_QUESTION_TYPES.SELECTION ? (question.optionsInput || "") : "",
                };
            }

            return { ...question, [field]: value };
        }));
    };

    const addSurveyQuestion = () => {
        setSurveyQuestions((prev) => [...prev, defaultSurveyQuestion()]);
    };


    useEffect(() => {
        if (!isSurveyRecord) {
            return;
        }

        const params = new URLSearchParams(location.search || "");
        if (params.get("view") !== "responses") {
            return;
        }

        window.setTimeout(() => {
            const responsesSection = document.getElementById("survey-responses-section");
            if (responsesSection) {
                responsesSection.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }, 250);
    }, [isSurveyRecord, location.search, surveyResponses.length]);

    const removeSurveyQuestion = (index) => {
        setSurveyQuestions((prev) => {
            const next = prev.filter((_, questionIndex) => questionIndex !== index);
            return next.length > 0 ? next : [defaultSurveyQuestion()];
        });
    };

    if (initialLoading) {
        return (
            <Container maxWidth="sm" sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                py: { xs: 4, md: 7 },
                background: "transparent",
            }}
        >
            <Container maxWidth="lg">
                <Paper
                    elevation={0}
                    sx={{
                        overflow: "hidden",
                        borderRadius: { xs: 4, md: 6 },
                        border: "1px solid rgba(112, 128, 185, 0.18)",
                        background: "rgba(255,255,255,0.9)",
                        boxShadow: "0 24px 60px rgba(54, 72, 120, 0.12)",
                        backdropFilter: "blur(12px)",
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2.5, md: 4.5 },
                            py: { xs: 3.5, md: 4.5 },
                            background: isWebsiteRecord
                                ? "radial-gradient(circle at top left, rgba(96, 165, 250, 0.18) 0%, transparent 34%), linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(168, 85, 247, 0.12) 100%)"
                                : "linear-gradient(135deg, rgba(79, 70, 229, 0.06) 0%, rgba(59, 130, 246, 0.08) 100%)",
                        }}
                    >
                        <Stack spacing={2.5}>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1.5}
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Chip
                                    icon={recordTypeIcon}
                                    label={recordTypeSummary.eyebrow}
                                    sx={{
                                        height: 36,
                                        borderRadius: 999,
                                        px: 1,
                                        fontWeight: 700,
                                        backgroundColor: "rgba(255,255,255,0.72)",
                                        border: "1px solid rgba(99, 102, 241, 0.14)",
                                        "& .MuiChip-icon": { color: "#4f46e5" },
                                    }}
                                />

                                <Button
                                    variant="text"
                                    onClick={() => navigate("/list")}
                                    sx={{
                                        fontWeight: 700,
                                        textTransform: "none",
                                        color: "#475569",
                                        alignSelf: { xs: "stretch", sm: "auto" },
                                    }}
                                >
                                    Back to records
                                </Button>
                            </Stack>

                            <Box>
                                <Typography
                                    variant={isMobile ? "h4" : "h3"}
                                    sx={{
                                        fontWeight: 800,
                                        letterSpacing: "-0.03em",
                                        color: "#0f172a",
                                        mb: 1.25,
                                    }}
                                >
                                    {pageTitle}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        maxWidth: 780,
                                        color: "#475569",
                                        fontSize: { xs: "1rem", md: "1.05rem" },
                                        lineHeight: 1.7,
                                    }}
                                >
                                    {recordTypeSummary.description}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Box sx={{ px: { xs: 2.5, md: 4.5 }, py: { xs: 3, md: 4 } }}>
                        <Stack direction={{ xs: "column", lg: "row" }} spacing={3.5} alignItems="stretch">
                            <Box sx={{ flex: "1 1 0" }}>
                                <Box
                                    component="form"
                                    onSubmit={handleSubmit}
                                    sx={{
                                        p: { xs: 2, md: 3 },
                                        borderRadius: 4,
                                        border: "1px solid rgba(112, 128, 185, 0.18)",
                                        backgroundColor: "rgba(255,255,255,0.72)",
                                        "& .MuiTextField-root": { mb: 2.25 },
                                    }}
                                >
                                    <TextField
                                        label="Record Name"
                                        value={recordName}
                                        fullWidth
                                        required
                                        variant="outlined"
                                        helperText={`${recordName.length}/${RECORD_NAME_MAX_LENGTH} characters`}
                                        inputProps={{ maxLength: RECORD_NAME_MAX_LENGTH }}
                                        onChange={(e) => setRecordName(e.target.value.slice(0, RECORD_NAME_MAX_LENGTH))}
                                    />

                                    {isFileRecord && (
                                        <TextField
                                            label="Upload New File"
                                            type="file"
                                            InputLabelProps={{ shrink: true }}
                                            onChange={(e) => setMenuFile(e.target.files?.[0] || null)}
                                            fullWidth
                                            variant="outlined"
                                            helperText="Leave this empty if you only want to rename the record."
                                        />
                                    )}

                                    {isUrlRecord && (
                                        <Stack spacing={1.75} sx={{ mb: 2.5, p: 2, borderRadius: 3, border: "1px solid rgba(99,102,241,0.2)", background: "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(245,248,255,0.9))" }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155" }}>
                                                URL record details
                                            </Typography>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="edit-url-type-label">QR Link Type</InputLabel>
                                                <Select
                                                    labelId="edit-url-type-label"
                                                    label="QR Link Type"
                                                    value={urlType}
                                                    onChange={(event) => {
                                                        const nextType = event.target.value;
                                                        setUrlType(nextType);
                                                        setUrlFields((prev) => ({ ...defaultUrlFields(), service: prev.service || "airbnb" }));
                                                    }}
                                                >
                                                    {SORTED_URL_TYPE_OPTIONS.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            {urlType === "url" && (
                                                <TextField
                                                    label="Enter URL"
                                                    value={urlFields.url}
                                                    onChange={(e) => setUrlFields((prev) => ({ ...prev, url: e.target.value }))}
                                                    fullWidth
                                                    required
                                                    variant="outlined"
                                                    helperText="Use the full destination URL, including https://."
                                                />
                                            )}

                                            {urlType === "whatsapp" && (
                                                <>
                                                    <TextField
                                                        label="WhatsApp number"
                                                        value={urlFields.waNumber}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, waNumber: e.target.value }))}
                                                        fullWidth
                                                        required
                                                        variant="outlined"
                                                    />
                                                    <TextField
                                                        label="Message"
                                                        value={urlFields.waMessage}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, waMessage: e.target.value }))}
                                                        fullWidth
                                                        multiline
                                                        minRows={3}
                                                        variant="outlined"
                                                    />
                                                </>
                                            )}

                                            {urlType === "sms" && (
                                                <>
                                                    <TextField
                                                        label="SMS number"
                                                        value={urlFields.smsNumber}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, smsNumber: e.target.value }))}
                                                        fullWidth
                                                        required
                                                        variant="outlined"
                                                    />
                                                    <TextField
                                                        label="SMS message"
                                                        value={urlFields.smsMessage}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, smsMessage: e.target.value }))}
                                                        fullWidth
                                                        multiline
                                                        minRows={3}
                                                        variant="outlined"
                                                    />
                                                </>
                                            )}

                                            {urlType === "phone" && (
                                                <TextField
                                                    label="Phone number"
                                                    value={urlFields.phone}
                                                    onChange={(e) => setUrlFields((prev) => ({ ...prev, phone: e.target.value }))}
                                                    fullWidth
                                                    required
                                                    variant="outlined"
                                                />
                                            )}

                                            {urlType === "email" && (
                                                <>
                                                    <TextField
                                                        label="Email address"
                                                        value={urlFields.email}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, email: e.target.value }))}
                                                        fullWidth
                                                        required
                                                        variant="outlined"
                                                    />
                                                    <TextField
                                                        label="Subject"
                                                        value={urlFields.subject}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, subject: e.target.value }))}
                                                        fullWidth
                                                        variant="outlined"
                                                    />
                                                    <TextField
                                                        label="Body"
                                                        value={urlFields.body}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, body: e.target.value }))}
                                                        fullWidth
                                                        multiline
                                                        minRows={3}
                                                        variant="outlined"
                                                    />
                                                </>
                                            )}

                                            {urlType === "geo" && (
                                                <>
                                                    <TextField
                                                        label="Latitude"
                                                        value={urlFields.latitude}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, latitude: e.target.value }))}
                                                        fullWidth
                                                        required
                                                        variant="outlined"
                                                    />
                                                    <TextField
                                                        label="Longitude"
                                                        value={urlFields.longitude}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, longitude: e.target.value }))}
                                                        fullWidth
                                                        required
                                                        variant="outlined"
                                                    />
                                                </>
                                            )}

                                            {urlType === "bitcoin" && (
                                                <>
                                                    <TextField
                                                        label="Wallet address"
                                                        value={urlFields.wallet}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, wallet: e.target.value }))}
                                                        fullWidth
                                                        required
                                                        variant="outlined"
                                                    />
                                                    <TextField
                                                        label="Amount (BTC)"
                                                        value={urlFields.amount}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, amount: e.target.value }))}
                                                        fullWidth
                                                        variant="outlined"
                                                    />
                                                    <TextField
                                                        label="Label"
                                                        value={urlFields.label}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, label: e.target.value }))}
                                                        fullWidth
                                                        variant="outlined"
                                                    />
                                                    <TextField
                                                        label="Message"
                                                        value={urlFields.message}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, message: e.target.value }))}
                                                        fullWidth
                                                        variant="outlined"
                                                    />
                                                </>
                                            )}

                                            {urlType === "facetime" && (
                                                <TextField
                                                    label="Phone or email"
                                                    value={urlFields.faceTimeHandle}
                                                    onChange={(e) => setUrlFields((prev) => ({ ...prev, faceTimeHandle: e.target.value }))}
                                                    fullWidth
                                                    required
                                                    variant="outlined"
                                                />
                                            )}

                                            {urlType === "service" && (
                                                <>
                                                    <FormControl fullWidth>
                                                        <InputLabel id="edit-social-service-label">Service</InputLabel>
                                                        <Select
                                                            labelId="edit-social-service-label"
                                                            label="Service"
                                                            value={urlFields.service}
                                                            onChange={(e) => setUrlFields((prev) => ({ ...prev, service: e.target.value }))}
                                                        >
                                                            {SOCIAL_SERVICE_OPTIONS.map((serviceOption) => (
                                                                <MenuItem key={serviceOption.value} value={serviceOption.value}>{serviceOption.label}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    <TextField
                                                        label="Handle / ID / slug"
                                                        value={urlFields.serviceHandle}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, serviceHandle: e.target.value }))}
                                                        fullWidth
                                                        required
                                                        variant="outlined"
                                                        helperText="Example: username, listing ID, or business slug."
                                                    />
                                                    <TextField
                                                        label="Or full custom URL"
                                                        value={urlFields.serviceCustomUrl}
                                                        onChange={(e) => setUrlFields((prev) => ({ ...prev, serviceCustomUrl: e.target.value }))}
                                                        fullWidth
                                                        variant="outlined"
                                                        helperText="Optional override if you already have a full link."
                                                    />
                                                </>
                                            )}
                                        </Stack>
                                    )}

                                    {isWebsiteRecord && (
                                        <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                                            <Alert
                                                severity="info"
                                                sx={{
                                                    borderRadius: 3,
                                                    alignItems: "center",
                                                    backgroundColor: "rgba(239, 246, 255, 0.88)",
                                                }}
                                            >
                                                Leave the website header or subheader blank to keep the default landing-page copy.
                                            </Alert>
                                            <TextField
                                                label="Website Header"
                                                value={websiteHeader}
                                                onChange={(e) => setWebsiteHeader(e.target.value)}
                                                fullWidth
                                                variant="outlined"
                                                helperText="This is the large headline visitors see first."
                                            />
                                            <TextField
                                                label="Website Sub Header"
                                                value={websiteSubheader}
                                                onChange={(e) => setWebsiteSubheader(e.target.value)}
                                                fullWidth
                                                multiline
                                                minRows={4}
                                                variant="outlined"
                                                helperText="Add a short sentence that explains what visitors can do from this page."
                                            />
                                            <Box sx={{ border: "1px solid rgba(148, 163, 184, 0.35)", borderRadius: 2.5, p: 1.5 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                                    Visible QR records on this website
                                                </Typography>
                                                {websiteRecordOptions.length === 0 ? (
                                                    <Typography variant="body2" color="text.secondary">No non-website records available yet.</Typography>
                                                ) : (
                                                    <List sx={{ p: 0 }}>
                                                        {websiteRecordOptions.map((record) => {
                                                            const checked = websiteVisibleRecordIds.includes(Number(record.id));
                                                            return (
                                                                <ListItem
                                                                    key={`website-visible-${record.id}`}
                                                                    dense
                                                                    disableGutters
                                                                    secondaryAction={(
                                                                        <Checkbox
                                                                            edge="end"
                                                                            checked={checked}
                                                                            onChange={(event) => {
                                                                                const recordId = Number(record.id);
                                                                                setWebsiteVisibleRecordIds((prev) => {
                                                                                    if (event.target.checked) {
                                                                                        return Array.from(new Set([...prev, recordId]));
                                                                                    }
                                                                                    return prev.filter((idValue) => idValue !== recordId);
                                                                                });
                                                                            }}
                                                                        />
                                                                    )}
                                                                >
                                                                    <ListItemText
                                                                        primary={record.name || `Record ${record.id}`}
                                                                        secondary={Number(record.CategoryID) === 4 ? "Survey" : Number(record.CategoryID) === 2 ? "URL" : "File"}
                                                                    />
                                                                </ListItem>
                                                            );
                                                        })}
                                                    </List>
                                                )}
                                            </Box>
                                        </Stack>
                                    )}

                                    {isSurveyRecord && (
                                        <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                                            <TextField
                                                label="Survey Header"
                                                value={surveyHeader}
                                                onChange={(e) => setSurveyHeader(e.target.value)}
                                                fullWidth
                                                variant="outlined"
                                                helperText="Main title shown at the top of the survey page."
                                            />
                                            <TextField
                                                label="Survey Sub Header"
                                                value={surveySubheader}
                                                onChange={(e) => setSurveySubheader(e.target.value)}
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                variant="outlined"
                                                helperText="Support text shown directly under the survey title."
                                            />
                                            <FormControlLabel
                                                control={(
                                                    <Switch
                                                        checked={surveyRequireLogin}
                                                        onChange={(event) => setSurveyRequireLogin(event.target.checked)}
                                                    />
                                                )}
                                                label="Require login to submit responses"
                                            />
                                            <TextField
                                                label="Bad-rating alert email"
                                                value={surveyAlertEmail}
                                                onChange={(event) => setSurveyAlertEmail(event.target.value)}
                                                type="email"
                                                fullWidth
                                                variant="outlined"
                                                helperText="For star, emoji, or thumbs ratings, an alert is sent here when a bad rating is submitted."
                                            />
                                            <Stack spacing={1.5}>
                                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                                                        Survey Questions
                                                    </Typography>
                                                    <Button
                                                        variant="text"
                                                        startIcon={<AddCircleOutlineRoundedIcon />}
                                                        onClick={addSurveyQuestion}
                                                        sx={{ textTransform: "none", fontWeight: 700 }}
                                                    >
                                                        Add question
                                                    </Button>
                                                </Stack>

                                                {surveyQuestions.map((question, index) => (
                                                    <Box
                                                        key={`survey-question-${index}`}
                                                        sx={{
                                                            p: 1.5,
                                                            border: "1px solid rgba(148, 163, 184, 0.35)",
                                                            borderRadius: 2.5,
                                                            backgroundColor: "rgba(255,255,255,0.9)",
                                                        }}
                                                    >
                                                        <Stack spacing={1.2}>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <TextField
                                                                    select
                                                                    label="Type"
                                                                    value={question.type}
                                                                    onChange={(event) => handleSurveyQuestionChange(index, "type", event.target.value)}
                                                                    sx={{ minWidth: 170 }}
                                                                >
                                                                    <MenuItem value={SURVEY_QUESTION_TYPES.TEXT}>Text response</MenuItem>
                                                                    <MenuItem value={SURVEY_QUESTION_TYPES.STAR}>Star review</MenuItem>
                                                                    <MenuItem value={SURVEY_QUESTION_TYPES.EMOJI}>Emoji review</MenuItem>
                                                                    <MenuItem value={SURVEY_QUESTION_TYPES.THUMBS}>Thumbs review</MenuItem>
                                                                    <MenuItem value={SURVEY_QUESTION_TYPES.SELECTION}>Selection review</MenuItem>
                                                                </TextField>
                                                                <IconButton
                                                                    aria-label={`Delete question ${index + 1}`}
                                                                    onClick={() => removeSurveyQuestion(index)}
                                                                    sx={{ ml: "auto" }}
                                                                >
                                                                    <DeleteOutlineRoundedIcon />
                                                                </IconButton>
                                                            </Stack>
                                                            <TextField
                                                                label={`Question ${index + 1}`}
                                                                value={question.label}
                                                                onChange={(event) => handleSurveyQuestionChange(index, "label", event.target.value)}
                                                                fullWidth
                                                                variant="outlined"
                                                                helperText="This label stays visible while respondents answer."
                                                            />
                                                            {question.type === SURVEY_QUESTION_TYPES.SELECTION && (
                                                                <TextField
                                                                    label="Selection options"
                                                                    value={question.optionsInput || ""}
                                                                    onChange={(event) => {
                                                                        const rawOptions = event.target.value;
                                                                        const options = rawOptions
                                                                            .split(",")
                                                                            .map((option) => option.trim())
                                                                            .filter(Boolean);
                                                                        setSurveyQuestions((prev) => prev.map((item, itemIndex) => (
                                                                            itemIndex === index
                                                                                ? { ...item, optionsInput: rawOptions, options }
                                                                                : item
                                                                        )));
                                                                    }}
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    helperText="Comma-separated options (for example: Excellent, Good, Fair)."
                                                                />
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Stack>
                                    )}

                                    <Accordion
                                        disableGutters
                                        elevation={0}
                                        sx={{
                                            mb: 2.5,
                                            border: "1px solid rgba(148, 163, 184, 0.35)",
                                            borderRadius: "14px !important",
                                            backgroundColor: "rgba(255,255,255,0.9)",
                                            "&::before": { display: "none" },
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreRoundedIcon />}
                                            aria-controls="password-protection-content"
                                            id="password-protection-header"
                                        >
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                                                Password protection
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Stack spacing={1.4}>
                                                <FormControlLabel
                                                    control={(
                                                        <Switch
                                                            checked={passwordProtected}
                                                            onChange={(event) => setPasswordProtected(event.target.checked)}
                                                        />
                                                    )}
                                                    label="Require a password before the QR destination opens"
                                                />
                                                {passwordProtected && (
                                                    <TextField
                                                        label={hasExistingPassword ? "Set a new password (optional)" : "Set password"}
                                                        type="password"
                                                        value={accessPassword}
                                                        onChange={(event) => setAccessPassword(event.target.value)}
                                                        fullWidth
                                                        variant="outlined"
                                                        helperText={hasExistingPassword
                                                            ? "Leave blank to keep the current password, or enter a new one to replace it."
                                                            : "Minimum 4 characters. This password is required before a scan can continue."}
                                                    />
                                                )}
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>

                                    <Accordion
                                        disableGutters
                                        elevation={0}
                                        sx={{
                                            mb: 2.5,
                                            border: "1px solid rgba(148, 163, 184, 0.35)",
                                            borderRadius: "14px !important",
                                            backgroundColor: "rgba(255,255,255,0.9)",
                                            "&::before": { display: "none" },
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreRoundedIcon />}
                                            aria-controls="completion-incentives-content"
                                            id="completion-incentives-header"
                                        >
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                                                Completion incentives
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Stack spacing={1.3}>
                                                <TextField
                                                    label="Gift voucher"
                                                    value={surveyCompletionRewards.giftVoucher}
                                                    onChange={(event) => setSurveyCompletionRewards((prev) => ({ ...prev, giftVoucher: event.target.value }))}
                                                    fullWidth
                                                    variant="outlined"
                                                    helperText="Optional gift voucher text to award when this record flow is completed."
                                                />
                                                <TextField
                                                    label="Discount code"
                                                    value={surveyCompletionRewards.discountCode}
                                                    onChange={(event) => setSurveyCompletionRewards((prev) => ({ ...prev, discountCode: event.target.value }))}
                                                    fullWidth
                                                    variant="outlined"
                                                    helperText="Optional discount code and redemption details."
                                                />
                                                <TextField
                                                    label="Prize to be won"
                                                    value={surveyCompletionRewards.prizeDraw}
                                                    onChange={(event) => setSurveyCompletionRewards((prev) => ({ ...prev, prizeDraw: event.target.value }))}
                                                    fullWidth
                                                    variant="outlined"
                                                    helperText="Optional prize draw info."
                                                />
                                                <TextField
                                                    label="Number of gifts allowed"
                                                    value={surveyCompletionRewards.giftLimit}
                                                    onChange={(event) => {
                                                        const sanitized = event.target.value.replace(/[^\d]/g, "");
                                                        setSurveyCompletionRewards((prev) => ({ ...prev, giftLimit: sanitized }));
                                                    }}
                                                    fullWidth
                                                    variant="outlined"
                                                    helperText="How many completions can receive a gift. Leave empty for unlimited."
                                                />
                                                <TextField
                                                    select
                                                    label="Gift assignment order"
                                                    value={surveyCompletionRewards.distributionMode}
                                                    onChange={(event) => setSurveyCompletionRewards((prev) => ({ ...prev, distributionMode: event.target.value }))}
                                                    fullWidth
                                                    variant="outlined"
                                                    helperText="Choose whether gifts are assigned sequentially or randomly."
                                                >
                                                    <MenuItem value="sequential">Sequential</MenuItem>
                                                    <MenuItem value="random">Random</MenuItem>
                                                </TextField>
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>

                                    <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            fullWidth
                                            size="large"
                                            disabled={loading}
                                            sx={{
                                                py: 1.75,
                                                borderRadius: 3,
                                                fontSize: "1rem",
                                                fontWeight: 700,
                                                textTransform: "none",
                                                background: "linear-gradient(135deg, #4f46e5 0%, #2563eb 100%)",
                                                boxShadow: "0 16px 30px rgba(79, 70, 229, 0.24)",
                                                "&:hover": {
                                                    boxShadow: "0 18px 34px rgba(37, 99, 235, 0.28)",
                                                },
                                            }}
                                        >
                                            {loading ? <CircularProgress size={24} color="inherit" /> : submitLabel}
                                        </Button>
                                    </motion.div>
                                </Box>

                                {message && (
                                    <Alert severity="success" sx={{ mt: 2.5, borderRadius: 3 }}>
                                        {message}
                                    </Alert>
                                )}
                                {errorMessage && (
                                    <Alert severity="error" sx={{ mt: 2.5, borderRadius: 3 }}>
                                        {errorMessage}
                                    </Alert>
                                )}
                            </Box>

                            <Box sx={{ width: { xs: "100%", lg: 320 }, flexShrink: 0 }}>
                                <Stack
                                    spacing={2}
                                    sx={{
                                        p: { xs: 2, md: 2.5 },
                                        borderRadius: 4,
                                        border: "1px solid rgba(112, 128, 185, 0.18)",
                                        background: "linear-gradient(180deg, rgba(248,250,255,0.96) 0%, rgba(241,245,255,0.96) 100%)",
                                        height: "100%",
                                    }}
                                >
                                    <Box>
                                        <Typography variant="overline" sx={{ color: "#4f46e5", fontWeight: 800, letterSpacing: "0.12em" }}>
                                            Editing guide
                                        </Typography>
                                        <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 800, color: "#0f172a" }}>
                                            Make this record easier to understand
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    {recordTypeSummary.tips.map((tip) => (
                                        <Stack key={tip} direction="row" spacing={1.2} alignItems="flex-start">
                                            <AutoAwesomeRoundedIcon sx={{ mt: "3px", color: "#6366f1", fontSize: 18 }} />
                                            <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.7 }}>
                                                {tip}
                                            </Typography>
                                        </Stack>
                                    ))}

                                    {(isWebsiteRecord || isSurveyRecord) && (
                                        <Box
                                            sx={{
                                                mt: 0.5,
                                                p: 2,
                                                borderRadius: 3,
                                                background: "linear-gradient(135deg, rgba(79,70,229,0.08) 0%, rgba(59,130,246,0.12) 100%)",
                                                border: "1px solid rgba(99,102,241,0.12)",
                                            }}
                                        >
                                            <Stack spacing={1.2}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <SubtitlesRoundedIcon sx={{ color: "#4f46e5", fontSize: 18 }} />
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                                        {isSurveyRecord ? "Survey page tip" : "Landing page preview tip"}
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.7 }}>
                                                    {isSurveyRecord
                                                        ? "Short questions with direct wording usually increase completion rates and improve response quality."
                                                        : "Strong website headers are usually short and action-oriented, while subheaders work best when they explain the value of the links underneath."}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                        {isSurveyRecord && (
                            <Box id="survey-responses-section" sx={{ mt: 3, p: { xs: 2, md: 3 }, borderRadius: 4, border: "1px solid rgba(112, 128, 185, 0.18)", background: "rgba(255,255,255,0.72)" }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>
                                    Survey responses
                                </Typography>
                                {responsesLoading ? (
                                    <CircularProgress size={22} />
                                ) : surveyResponses.length === 0 ? (
                                    <Alert severity="info">No survey responses yet.</Alert>
                                ) : (
                                    <List sx={{ p: 0 }}>
                                        {surveyResponses.map((response) => (
                                            <ListItem key={response.id} sx={{ display: "block", border: "1px solid rgba(148, 163, 184, 0.2)", borderRadius: 2, mb: 1.25 }}>
                                                <ListItemText
                                                    primary={`Response #${response.id}`}
                                                    secondary={new Date(response.created_at).toLocaleString()}
                                                />
                                                <Stack spacing={1}>
                                                    {(response.answers || []).map((answer, index) => (
                                                        <Typography key={`${response.id}-${index}`} variant="body2" sx={{ color: "#334155" }}>
                                                            <strong>{answer.question}</strong>: {answer.answer}
                                                        </Typography>
                                                    ))}
                                                </Stack>
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default EditCatalogPage;
